/**
 * PLAT-11781 — where to seek after adding a composition overlay so the user
 * actually sees it.
 *
 * Text and shape graphics insert at the playhead, but a composition's time is
 * dictated by its template placement (Start / End / Section / clip), so it can
 * land well away from the frame on the canvas and look like nothing happened.
 * Pure: takes the placed element's timing and returns the seek target, or
 * `null` when the playhead should stay where it is.
 *
 * The target is a fifth of the way in. Most cards open on a transparent
 * background and build in over their first seconds, so a frame near the start
 * still shows nothing, while the midpoint (what the transcript's quick-edit
 * popover seeks to) pulls the user too far from where they were working on a
 * clip-long overlay. That blank start includes the default placement, which
 * inserts AT the playhead: the playhead is then inside the overlay yet on its
 * blank first frame, so being inside is no reason to stay.
 */

/** Fraction of the overlay's duration to land at. */
export const OVERLAY_REVEAL_FRACTION = 0.2;

export interface OverlayRevealInput {
  /** Overlay start, seconds on the timeline. */
  time: number;
  /** Overlay length in seconds. */
  durationSeconds: number;
  /** Seeking under an active playback would yank the user out of it. */
  isPlaying: boolean;
}

export function resolveOverlayRevealTime({
  time,
  durationSeconds,
  isPlaying,
}: OverlayRevealInput): number | null {
  if (isPlaying) return null;
  if (!Number.isFinite(time) || !(durationSeconds > 0)) return null;
  return time + durationSeconds * OVERLAY_REVEAL_FRACTION;
}
